import AppResource from '@milestoneslabs/milestones-js-engine/src/Data/AppResource';
import OrganizationAppResource from "./OrganizationAppResource";
import OrganizationRoleAppResource from "./OrganizationRoleAppResource";
import OrganizationMemberAppResource from "./OrganizationMemberAppResource";
import WorkspaceAppResource from "./WorkspaceAppResource";
import WorkspaceRoleAppResource from "./WorkspaceRoleAppResource";
import LuWorkspaceMembershipTypeAppResource from "./LuWorkspaceMembershipTypeAppResource";
import LuOrganizationOccupationAppResource from "./LuOrganizationOccupationAppResource";
import LuDeclineInvitationReasonAppResource from "./LuDeclineInvitationReasonAppResource";
import Engine from "@milestoneslabs/milestones-js-engine/src/Engine";
import isEmpty from "lodash/isEmpty";
import PendingHubDetailsAppResource
    from "@milestoneslabs/milestones-core-js/src/AppResources/PendingHubDetailsAppResource";

export const INVITATION_STATUS = Object.freeze({
    PENDING_INVITATION: 'pendingInvitation',
    PENDING_CONFIRMATION: 'pendingConfirmation',
    CONFIRMED: 'confirmed',
    REVOKED: 'revoked',
    EXPIRED: 'expired',
    DECLINED: 'declined',
    JOINED: 'joined',
});

class UserInvitationAppResource extends AppResource {

    get displaySettings() {
        return typeof this._displaySettings === 'undefined' ?
            {
                location: null,
                dismissible: true,
                disabledFields: [],
            } :
            this._displaySettings
    }

    set displaySettings(value) {
        this._displaySettings = {
            location: value.hasOwnProperty('location') ? value.location : null,
            dismissible: value.hasOwnProperty('dismissible') ? value.dismissible : true,
            disabledFields: value.hasOwnProperty('disabledFields') ? value.disabledFields : [],
            userUuid:  value.hasOwnProperty('userUuid') ? value.userUuid : null,
        };
    }

    /**
     * @returns {string}
     */
    appResourceType() {
        return 'User-Invitation';
    }

    /**
     * @returns {string}
     */
    appResourceApiPrefix() {
        return 'users/invitations';
    }

    /**
     * @returns {*[]}
     */
    hidden() {
        return super.hidden().concat([
            'uuid',
            'deleted_at',
        ]);
    }

    /**
     * @returns {*[]}
     */
    fillable() {
        return [
            'key',
            'email',
            'firstName',
            'lastName',
            'status',
            'organizationUuid',
            'organizationRoleUuid',
            'invitedByOrganizationMemberUuid',
            'workspaceUuid',
            'workspaceRoleUuid',
            'workspaceMembershipTypeUuid',
            'organization',
            'organizationRole',
            'invitedByOrganizationMember',
            'workspace',
            'workspaceRole',
            'workspaceMembershipType',
            'organizationOccupationId',
            'organizationOccupation',
            'declineReason',
            'declineReasonId',
            'declineReasonLookup',
            'fullName',
            'reminderInitialDate',
            'valid',
            'moxiAgentUuid',
            'moxiCompanyId',
            'pendingHubDetailsUuid',
            'pendingHubDetails',
        ];
    }

    /**
     * @returns {*[]}
     */
    relationMap() {
        return {
            organization: OrganizationAppResource,
            organizationRole: OrganizationRoleAppResource,
            invitedByOrganizationMember: OrganizationMemberAppResource,
            workspace: WorkspaceAppResource,
            workspaceRole: WorkspaceRoleAppResource,
            workspaceMembershipType: LuWorkspaceMembershipTypeAppResource,
            organizationOccupation: LuOrganizationOccupationAppResource,
            declineReasonLookup: LuDeclineInvitationReasonAppResource,
            pendingHubDetails: PendingHubDetailsAppResource,
        };
    }

    /**
     * @returns {*[]}
     */
    relationForeignKeys() {
        return {
            organization: 'organization_uuid',
            organizationRole: 'organization_role_uuid',
            invitedByOrganizationMember: 'invited_by_organization_member_uuid',
            workspace: 'workspace_uuid',
            workspaceRole: 'workspace_role_uuid',
            workspaceMembershipType: 'workspace_membership_type_uuid',
            organizationOccupation: 'organization_occupation_id',
            declineReasonLookup: 'decline_reason_id',
            pendingHubDetails: 'pending_hub_details_uuid'
        };
    }

    /**
     * @returns {*}
     */
    validationRules() {
        return {
            key: {
                type: 'string',
                length: {
                    minimum: 1,
                },
            },
            email: {
                type: 'string',
                email: true,
            },
            status: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            firstName: {
                type: 'string',
                length: {
                    minimum: 2,
                    maximum: 255,
                },
                includesLetter: true,
            },
            lastName: {
                type: 'string',
                length: {
                    minimum: 2,
                    maximum: 255,
                },
                includesLetter: true,
            },
            organizationUuid: {
                type: 'string',
            },
            organizationRoleUuid: {
                type: 'string',
            },
            invitedByOrganizationMemberUuid: {
                type: 'string',
            },
            workspaceUuid: {
                type: 'string',
            },
            workspaceRoleUuid: {
                type: 'string',
            },
            workspaceMembershipTypeUuid: {
                type: 'string',
            },
            organizationOccupationId: {
                type: 'integer',
            },
            declineReasonId: {
                type: 'integer',
            },
            declineReason: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            fullName: {
                type: 'string',
            },
            reminderInitialDate: {
                datetime: true,
            },
            valid: {
                type: 'boolean',
            },
            moxiAgentUuid: {
                type: 'string',
            },
            moxiCompanyId: {
                type: 'string',
            },
        };
    }

    apiPaths() {
        return {
            ...super.apiPaths(),
            acceptInvitation: Engine.apiUrl(`${this.appResourceApiPrefix()}/accept-invitation`),
            filesUpload: Engine.apiUrl(`${this.appResourceApiPrefix()}/files/upload`),
            resend: Engine.apiUrl(`${this.appResourceApiPrefix()}/{uuid}/resend`),
        };
    }

    async acceptInvitation() {
        if (!this.uuid) {
            throw new Error('Invitation uuid is missing!');
        }
        const apiResponse = await this.enginePost(this.apiPaths().acceptInvitation, {
            invitationUuid: this.uuid
        });

        return apiResponse;
    }

    async resend() {
        if (this.uuid === null) {
            throw new Error('Uuid is missing!');
        }

        let updateData = {};

        if (this.isDirty) {
            const validationErrors = this.validateExisting(false);
            if (!isEmpty(validationErrors)) {
                if (Engine.debug) {
                    const resourceType = this.resourceType;
                    console.warn(
                        'Cannot update invalid ' + resourceType,
                        validationErrors,
                    )
                }
                return Promise.reject('Attributes validation not passed!')
            }
            updateData = this.buildUpdateData(false)
        }

        const url = this.apiPaths().resend.replace('{uuid}', this.uuid);

        this._emitter.emitSync('beforeResend', this);
        const apiResponse = await this.enginePost(url, updateData);
        this._emitter.emit('resent', this, apiResponse);

        return apiResponse
    }

    async fileUpload(params) {
        // Prepare form data values
        const formData = new FormData();
        formData.append('file', params.file);
        formData.append('organizationUuid', params.organizationUuid);

        // Make request
        this._emitter.emitSync('beforeFileUpload', this);
        const apiResponse = await this.enginePost(this.apiPaths().filesUpload, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        this._emitter.emit('fileUpload', this, apiResponse);

        return apiResponse
    }
}

export default UserInvitationAppResource;
